import React, { useState, useCallback } from "react";
import { FaDice, FaBackspace, FaCheck } from "react-icons/fa";
import "./LuckyKeypad.css";

/**
 * LuckyKeypad - A retro arcade-style number input for selecting choice (1-65535)
 * Features neon buttons that light up when pressed and a randomize function
 * Updated for v2 CandyDispenser: u16 range (1-65535) instead of u8 (1-255)
 */
const LuckyKeypad = ({ onSelect, disabled = false }) => {
  const [displayValue, setDisplayValue] = useState("");
  const [lastPressed, setLastPressed] = useState(null);

  // Handle number button press
  const handleNumberPress = useCallback(
    (num) => {
      if (disabled) return;

      setLastPressed(num);
      setTimeout(() => setLastPressed(null), 150);

      setDisplayValue((prev) => {
        const newValue = prev + num;
        const numericValue = parseInt(newValue, 10);

        // Limit to 65535 (u16 max)
        if (numericValue > 65535) {
          return "65535";
        }

        // Remove leading zeros
        if (newValue.length > 1 && newValue.startsWith("0")) {
          return newValue.substring(1);
        }

        // Max 5 digits
        if (newValue.length > 5) {
          return prev;
        }

        return newValue;
      });
    },
    [disabled]
  );

  // Handle backspace
  const handleBackspace = useCallback(() => {
    if (disabled) return;
    setLastPressed("back");
    setTimeout(() => setLastPressed(null), 150);
    setDisplayValue((prev) => prev.slice(0, -1));
  }, [disabled]);

  // Handle randomize
  const handleRandomize = useCallback(() => {
    if (disabled) return;

    const finalNum = Math.floor(Math.random() * 65535) + 1;
    setDisplayValue(finalNum.toString());
    setLastPressed("random");
    setTimeout(() => setLastPressed(null), 150);
  }, [disabled]);

  // Handle confirm
  const handleConfirm = useCallback(() => {
    if (disabled) return;

    const numericValue = parseInt(displayValue, 10);

    if (!displayValue || numericValue < 1 || numericValue > 65535) {
      // Flash error state
      setDisplayValue("");
      return;
    }

    setLastPressed("confirm");
    setTimeout(() => setLastPressed(null), 150);

    if (onSelect) {
      onSelect(numericValue);
    }
  }, [disabled, displayValue, onSelect]);

  // Clear display
  const handleClear = useCallback(() => {
    if (disabled) return;
    setDisplayValue("");
  }, [disabled]);

  // Render keypad buttons
  const renderButtons = () => {
    const buttons = [];

    // Numbers 1-9
    for (let i = 1; i <= 9; i++) {
      buttons.push(
        <button
          key={i}
          className={`keypad-btn number-btn ${lastPressed === i.toString() ? "pressed" : ""
            }`}
          onClick={() => handleNumberPress(i.toString())}
          disabled={disabled}
        >
          {i}
        </button>
      );
    }

    // Random, 0, Backspace
    buttons.push(
      <button
        key="random"
        className={`keypad-btn action-btn random-btn ${lastPressed === "random" ? "pressed" : ""
          }`}
        onClick={handleRandomize}
        disabled={disabled}
        title="Randomize"
      >
        <FaDice />
      </button>
    );

    buttons.push(
      <button
        key="0"
        className={`keypad-btn number-btn ${lastPressed === "0" ? "pressed" : ""
          }`}
        onClick={() => handleNumberPress("0")}
        disabled={disabled}
      >
        0
      </button>
    );

    buttons.push(
      <button
        key="back"
        className={`keypad-btn action-btn back-btn ${lastPressed === "back" ? "pressed" : ""
          }`}
        onClick={handleBackspace}
        disabled={disabled}
        title="Backspace"
      >
        <FaBackspace />
      </button>
    );

    return buttons;
  };

  return (
    <div className={`lucky-keypad ${disabled ? "disabled" : ""}`}>
      {/* Display */}
      <div className="keypad-display">
        <div className="display-label">LUCKY VECTOR</div>
        <div className="display-value">
          {displayValue || "-----"}
        </div>
        <div className="display-range">1 - 65535</div>
      </div>

      {/* Keypad Grid */}
      <div className="keypad-grid">{renderButtons()}</div>

      {/* Confirm Button */}
      <button
        className={`confirm-btn ${lastPressed === "confirm" ? "pressed" : ""} ${!displayValue ? "inactive" : ""
          }`}
        onClick={handleConfirm}
        disabled={disabled || !displayValue}
      >
        <FaCheck />
        <span>LOCK IN</span>
      </button>

      {/* Quick picks */}
      <div className="quick-picks">
        <span className="quick-label">Quick Pick:</span>
        {[69, 420, 777, 1337, 65535].map((num) => (
          <button
            key={num}
            className="quick-btn"
            onClick={() => {
              if (!disabled) {
                setDisplayValue(num.toString());
              }
            }}
            disabled={disabled}
          >
            {num}
          </button>
        ))}
      </div>
    </div>
  );
};

export default LuckyKeypad;
